import { successToast } from '@components/toasts/Toasts';
import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useFeatureFlagContext } from 'restaurantAdmin/featureFlags';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  updateTemporalOccupancyRatings,
  UpdateTemporalOccupancyRatingsPayload,
} from './apiHelpers';
import { PricingPageNavLinks } from './PricingPageNavLinks';
import {
  DAYS,
  MONTHS,
  MONTH_OCCUPANCY_RATING_VALUE_LEVELS,
  OCCUPANCY_RATING_VALUE_LEVELS,
  PeakPersonalizationChart,
  PeakPersonalizationChartDaily,
  apiToWeeklyPeaks,
  apiToYearlyPeaks,
  peaksToPayload,
  apiToDailyPeaks,
  buildTimeOfDayRatingColumnDefsDaily,
  validateHasUnsavedPeakChanges,
} from './peakPersonalizationChart';
import { Button, ButtonVariants } from '@components/button/Button';
import { errorToast } from '@components/toasts/Toasts';
import { useTemporalOccupancyRatings } from './hooks';
import { splitShiftsAtMidnight } from '@shared/utils/shifts';
import { PeakPersonalizations, TimeOfDayOccupancyRating } from './types';
import { useShifts } from '../shifts/hooks';
import { Divider } from '@components/divider/Divider';
import { IconButton } from '@components/iconButton/IconButton';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { SelectPeaksDaysModal } from './SelectPeaksDaysModal';
import styles from './BusyTimesPage.scss';

export const BusyTimesPage = () => {
  const queryClient = useQueryClient();
  const { id: restaurantId, availabilityInterval } = useRestaurant();
  const { data: shifts } = useShifts({ includeEnded: false });
  const { isEnabled } = useFeatureFlagContext();
  const {
    open: openPeakPersonalizationModal,
    close: closePeakPersonalizationModal,
    isOpen: isPeakPersonalizationModalOpen,
  } = useIsOpen();

  const [selectedPeakDay, setSelectedPeakDay] = useState<number>(0);
  const [weeklyPeaks, setWeeklyPeaks] = useState<number[]>(Array(7).fill(0));
  const [dailyPeaks, setDailyPeaks] = useState<
    Omit<TimeOfDayOccupancyRating, 'id'>[]
  >([]);
  const [yearlyPeaks, setYearlyPeaks] = useState<number[]>(Array(12).fill(0));
  const selectedPeakDayRatings = useMemo(() => {
    return dailyPeaks.filter((peak) => peak.dayOfWeek === selectedPeakDay);
  }, [dailyPeaks, selectedPeakDay]);

  const {
    data: temporalOccupancyRatings,
    isSuccess: isTemporalRatingsSuccess,
  } = useTemporalOccupancyRatings();

  const dailyHoursColumnDefs = useMemo(
    () =>
      buildTimeOfDayRatingColumnDefsDaily(
        availabilityInterval,
        splitShiftsAtMidnight(shifts),
      ),
    [
      availabilityInterval,
      shifts,
      buildTimeOfDayRatingColumnDefsDaily,
      splitShiftsAtMidnight,
    ],
  );

  const savedPeaks = useMemo<PeakPersonalizations>(
    () => ({
      dailyPeaks: apiToDailyPeaks(temporalOccupancyRatings),
      weeklyPeaks: apiToWeeklyPeaks(temporalOccupancyRatings),
      yearlyPeaks: apiToYearlyPeaks(temporalOccupancyRatings),
    }),
    [
      temporalOccupancyRatings,
      apiToDailyPeaks,
      apiToWeeklyPeaks,
      apiToYearlyPeaks,
    ],
  );

  const userHasUnsavedChanges = useMemo(
    () =>
      validateHasUnsavedPeakChanges(
        { dailyPeaks, weeklyPeaks, yearlyPeaks },
        savedPeaks,
      ),
    [
      dailyPeaks,
      weeklyPeaks,
      yearlyPeaks,
      savedPeaks,
      validateHasUnsavedPeakChanges,
    ],
  );

  useEffect(() => {
    if (isTemporalRatingsSuccess && temporalOccupancyRatings) {
      setDailyPeaks(apiToDailyPeaks(temporalOccupancyRatings));
      setWeeklyPeaks(apiToWeeklyPeaks(temporalOccupancyRatings));
      setYearlyPeaks(apiToYearlyPeaks(temporalOccupancyRatings));
    }
  }, [isTemporalRatingsSuccess, temporalOccupancyRatings]);

  const updateTemporalOccupancyRatingsMutation = useMutation({
    mutationFn: (payload: UpdateTemporalOccupancyRatingsPayload) =>
      updateTemporalOccupancyRatings(restaurantId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['restaurant', restaurantId, 'temporalOccupancyRatings'],
      });
      successToast({ message: 'Peak personalization saved' });
    },
    onError: () => {
      errorToast({
        message:
          'An error occurred saving peak personalization. Please try again.',
      });
    },
  });

  const handleSavePeakPersonalization = useCallback(() => {
    updateTemporalOccupancyRatingsMutation.mutate(
      peaksToPayload(dailyPeaks, weeklyPeaks, yearlyPeaks),
    );
  }, [
    dailyPeaks,
    weeklyPeaks,
    yearlyPeaks,
    updateTemporalOccupancyRatingsMutation,
  ]);

  const clearDailySelectedDay = useCallback(() => {
    setDailyPeaks((prev) =>
      prev.map((peak) => ({
        ...peak,
        rating: selectedPeakDay === peak.dayOfWeek ? 0 : peak.rating,
      })),
    );
  }, [setDailyPeaks, selectedPeakDay]);

  const clearWeekly = useCallback(() => {
    setWeeklyPeaks(Array(7).fill(0));
  }, []);

  const clearYearly = useCallback(() => {
    setYearlyPeaks(Array(12).fill(0));
  }, []);

  const updateWeeklyPeak = useCallback(
    (index: number, heightPercent: number) => {
      setWeeklyPeaks((prev) => {
        const next = [...prev];
        next[index] = heightPercent;
        return next;
      });
    },
    [],
  );

  const updateYearlyPeak = useCallback(
    (index: number, heightPercent: number) => {
      setYearlyPeaks((prev) => {
        const next = [...prev];
        next[index] = heightPercent;
        return next;
      });
    },
    [],
  );

  const updateDailyPeak = useCallback(
    (time: string, rating: number) => {
      setDailyPeaks((prev) => {
        const next = [...prev];
        const existingIndex = next.findIndex(
          (peak) => peak.time === time && peak.dayOfWeek === selectedPeakDay,
        );

        if (existingIndex !== -1) {
          next[existingIndex].rating = rating;

          return next;
        }

        next.push({ time, rating, dayOfWeek: selectedPeakDay });

        return next;
      });
    },
    [selectedPeakDay],
  );

  const copyPeakPersonalization = useCallback(
    (selectedDays: number[]) => {
      setDailyPeaks((prev) => {
        const next = [...prev];

        selectedDays.forEach((day) => {
          next.forEach((peak) => {
            if (peak.dayOfWeek === day) {
              peak.rating = 0;
            }
          });
        });

        const nextSelectedPeakDayRatings = next.filter(
          (peak) => peak.dayOfWeek === selectedPeakDay,
        );

        nextSelectedPeakDayRatings.forEach((peak) => {
          selectedDays.forEach((day) => {
            const existingIndex = next.findIndex(
              (p) => p.time === peak.time && p.dayOfWeek === day,
            );
            if (existingIndex !== -1) {
              next[existingIndex].rating = peak.rating;
            } else {
              next.push({
                time: peak.time,
                rating: peak.rating,
                dayOfWeek: day,
              });
            }
          });
        });

        return next;
      });
    },
    [selectedPeakDay, setDailyPeaks],
  );

  return (
    <>
      <PageHeader title="Pricing" category="Operations">
        <PricingPageNavLinks
          userHasUnsavedChanges={userHasUnsavedChanges}
          saveChanges={handleSavePeakPersonalization}
          modalMessage={
            <p>
              You have unsaved changes to your peak personalization. Please save
              your work before navigating to a different page.
            </p>
          }
        />
        <Button
          label="Save and Apply"
          onClick={handleSavePeakPersonalization}
          variant={ButtonVariants.Primary}
          isDisabled={!userHasUnsavedChanges}
        />
      </PageHeader>
      <PageContent
        style={{ gap: '1.5rem', display: 'flex', flexDirection: 'column' }}
      >
        <div className={styles.pricingPageContent}>
          <section className={styles.section}>
            <div className={styles.header}>
              <div className={styles.checkboxContainer} role="group">
                <h2 className={styles.sectionTitle}>Peaks Daily</h2>
                <ul>
                  {DAYS.map((day, index) => (
                    <li key={day}>
                      <div
                        role="checkbox"
                        aria-checked={selectedPeakDay === index}
                        onClick={() => setSelectedPeakDay(index)}
                      >
                        {day}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <IconButton
                ariaLabel="add peak personalization"
                iconName="copy"
                onClick={openPeakPersonalizationModal}
                iconClassName={styles.icon}
              />
            </div>
            <PeakPersonalizationChartDaily
              data={selectedPeakDayRatings}
              labels={dailyHoursColumnDefs}
              onDataChange={updateDailyPeak}
              instruction="Click and drag to set busy hours throughout day."
              onClearAll={clearDailySelectedDay}
              valueLevels={OCCUPANCY_RATING_VALUE_LEVELS}
            />
          </section>
        </div>
        <div className={styles.pricingPageContent}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Peaks Weekly</h2>
            <PeakPersonalizationChart
              data={weeklyPeaks}
              labels={DAYS}
              onDataChange={updateWeeklyPeak}
              instruction="Click and drag to set busy days of the week."
              onClearAll={clearWeekly}
              valueLevels={OCCUPANCY_RATING_VALUE_LEVELS}
            />
          </section>
          <Divider className={styles.divider} />
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Peaks Yearly</h2>
            <PeakPersonalizationChart
              data={yearlyPeaks}
              labels={MONTHS}
              onDataChange={updateYearlyPeak}
              instruction="Click and drag to set busy months of the year."
              onClearAll={clearYearly}
              valueLevels={MONTH_OCCUPANCY_RATING_VALUE_LEVELS}
            />
          </section>
          <Divider className={styles.divider} />
        </div>
      </PageContent>
      <SelectPeaksDaysModal
        selectedDay={selectedPeakDay}
        isOpen={isPeakPersonalizationModalOpen}
        onClose={closePeakPersonalizationModal}
        onCreate={(selectedDays: number[]) => {
          copyPeakPersonalization(selectedDays);
          closePeakPersonalizationModal();
        }}
      />
    </>
  );
};
